import { Action, Selector, State, StateContext } from "@ngxs/store";
import { TrackRequestModel, TrackStateModel } from "./track.model";
import { LoadingState } from "@tsin-core/entities";
import { inject, Injectable } from "@angular/core";
import { TrackService } from "@tsin-core/services/http/track.service";
import { tap, catchError } from "rxjs";
import { CreateTrack, DeleteTrack, SetFilteredTracks, SetTrackSearch, Track, Tracks, UpdateTrack } from "./track.action";

@State<TrackStateModel>({
  name: 'trackState',
  defaults: {
    searchQuery: '',
    filteredTracks: '',
    tracks: [],
    track: null,
    loading: LoadingState.notLoading
  }
})

@Injectable()
export class TrackState {

  private trackService = inject(TrackService)

  @Selector()
  static getTracks(state: TrackStateModel) {
    return state.tracks;
  }

  @Selector()
  static getLoading(state: TrackStateModel) {
    return state.loading;
  }

  @Selector()
  static filteredItems(state: TrackStateModel) {
    const query = state.searchQuery;
    if (typeof query === 'object' && 'cohortId' in query) {
      if (query?.cohortId !== undefined) {
        return state.tracks.filter((track: any) =>
          track?.cohort?.id === query?.cohortId
        );
      } else {
        return state.tracks;
      }
    } else if (typeof query === 'object' && 'administrationId' in query) {
      if (query?.administrationId !== undefined) {
        return state.tracks.filter((track: any) =>
          track?.administration?.id === query?.administrationId
        );
      } else {
        return state.tracks;
      }
    } else if (typeof query === 'object' && 'cohortId' in query && 'administrationId' in query) {
      if (query?.cohortId !== undefined && query?.administrationId !== undefined) {
        return state.tracks.filter((track: any) =>
          track?.cohort?.id === query?.cohortId &&
          track?.administration?.id === query?.administrationId
        );
      } else {
        return state.tracks;
      }
    } else {
      if (!query || query === undefined) {
        return state.tracks;  // Return all tracks when searchQuery is empty
      }
      return state.tracks.filter((track: any) =>
        track?.cohort?.id?.includes((query as string).toLowerCase()) ||
        track?.administration?.id?.includes((query as string).toLowerCase()) ||
        track?.track?.id?.includes((query as string).toLowerCase()) ||
        track?.name?.toLowerCase().includes((query as string).toLowerCase()) ||
        track?.dayTime?.toLowerCase().includes((query as string).toLowerCase()) ||
        track?.trackNo?.toLowerCase().includes((query as string).toLowerCase())
      );
    }
  }

  @Selector()
  static filteredTracks(state: TrackStateModel) {
    const filter = state.filteredTracks;
    if (!filter || filter === undefined) {
      return [];  // Return all administrations when searchQuery is empty
    }
    return state.tracks.filter((track) => track.administrationId === filter);
  }

  @Action(Tracks)
  getTracks({ patchState }: StateContext<TrackStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.trackService.getTracks().pipe(
      tap((tracks: TrackRequestModel[]) => {
        patchState({ tracks: tracks, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(Track)
  getTrack(ctx: StateContext<TrackStateModel>, action: Track) {
    return this.trackService.getTrack(action.payload).pipe(
      tap((track: TrackRequestModel) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          track: track,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  @Action(CreateTrack)
  createTrack({ patchState, getState }: StateContext<TrackStateModel>, action: CreateTrack) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.trackService.createTrack(action.payload).pipe(
      tap((result: TrackRequestModel) => {
        const state = getState();
        patchState({
          tracks: [...state.tracks, result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateTrack)
  updateTrack({ patchState, getState }: StateContext<TrackStateModel>, action: UpdateTrack) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.trackService.updateTrack(action.payload).pipe(
      tap((result) => {
        let tracks = getState().tracks;
        const index = tracks.findIndex((track) => track.id === result.id);
        tracks[index] = result;
        patchState({
          tracks,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  @Action(DeleteTrack)
  deleteTrack({ patchState, getState }: StateContext<TrackStateModel>, action: DeleteTrack) {
    patchState({ loading: LoadingState.loadingDelete });
    return this.trackService.deleteTrack(action.payload).pipe(
      tap(() => {
        let tracks = getState().tracks;
        tracks = tracks.filter(
          (track) => track.id !== action.payload
        );
        patchState({
          tracks,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  // Action to set the search query
  @Action(SetTrackSearch)
  setSearchQuery(ctx: StateContext<TrackStateModel>, action: SetTrackSearch) {
    ctx.patchState({ loading: LoadingState.loadingList });
    setTimeout(() => {
      ctx.patchState({
        searchQuery: action.query,
        loading: LoadingState.notLoading
      });
    }, 1000)
  }

  @Action(SetFilteredTracks)
  SetFilteredTracks(ctx: StateContext<TrackStateModel>, action: SetFilteredTracks) {
    ctx.patchState({ loading: LoadingState.loadingList });
    setTimeout(() => {
      ctx.patchState({
        filteredTracks: action.payload,
        loading: LoadingState.notLoading
      });
    }, 1000)
  }

}
